import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { inquiryService } from '@/services/storage';
import { toast } from 'sonner';

export default function SampleApply() {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    company: '',
    province: '',
    city: '',
    productModel: '',
    quantity: '',
    estimatedUsage: '',
    description: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.name || !formData.phone || !formData.email || !formData.productModel) {
      toast.error('请填写必填项');
      return;
    }

    // Create inquiry
    const inquiry = {
      id: Date.now().toString(),
      ...formData,
      status: 'pending' as const,
      createdAt: new Date().toISOString(),
    };

    inquiryService.add(inquiry);
    setSubmitted(true);
    toast.success('申请提交成功，我们会尽快与您联系！');
  };

  if (submitted) {
    return (
      <div className="pt-20">
        <section className="py-20">
          <div className="container mx-auto px-4">
            <Card className="max-w-2xl mx-auto">
              <CardContent className="p-12 text-center">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center text-green-600 mx-auto mb-6">
                  <CheckCircle className="h-10 w-10" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  申请提交成功！
                </h2>
                <p className="text-gray-600 mb-8">
                  收到您的预约咨询后，我们将会与您进行深度需求沟通。
                  <br />
                  只需提供一些细节，我们就能回复您的询问。
                </p>
                <div className="flex justify-center space-x-4">
                  <Button onClick={() => navigate('/')}>返回首页</Button>
                  <Button variant="outline" onClick={() => setSubmitted(false)}>
                    继续申请
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="pt-20">
      {/* Hero Banner */}
      <section className="bg-gradient-to-r from-blue-900 to-blue-700 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              样品申请与购买
            </h1>
            <p className="text-xl text-blue-100">
              收到您的预约咨询后将会与您进行深度需求沟通
            </p>
            <p className="text-blue-200 mt-2">
              After receiving your reservation, we will communicate with you about your needs in depth
            </p>
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <Card className="max-w-4xl mx-auto">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">基本信息</h2>
              <p className="text-gray-600 mb-8">
                请按如下表格填写联系方式和您的诉求，我们会尽快反馈并与您联系。
              </p>

              <form onSubmit={handleSubmit}>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <Label htmlFor="name">
                      姓名 <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="请输入您的姓名"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone">
                      电话 <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="请输入您的电话"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">
                      邮箱 <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="请输入您的邮箱"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="company">公司</Label>
                    <Input
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="请输入您的公司名称"
                    />
                  </div>

                  <div>
                    <Label htmlFor="province">省份</Label>
                    <Input
                      id="province"
                      name="province"
                      value={formData.province}
                      onChange={handleChange}
                      placeholder="请输入省份"
                    />
                  </div>

                  <div>
                    <Label htmlFor="city">城市</Label>
                    <Input
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      placeholder="请输入城市"
                    />
                  </div>
                </div>

                <div className="border-t pt-6 mb-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">所需支持</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="productModel">
                        样品号 <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="productModel"
                        name="productModel"
                        value={formData.productModel}
                        onChange={handleChange}
                        placeholder="请输入产品型号，如HP1117"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="quantity">数量</Label>
                      <Input
                        id="quantity"
                        name="quantity"
                        value={formData.quantity}
                        onChange={handleChange}
                        placeholder="请输入所需数量"
                      />
                    </div>

                    <div>
                      <Label htmlFor="estimatedUsage">预估用量</Label>
                      <Input
                        id="estimatedUsage"
                        name="estimatedUsage"
                        value={formData.estimatedUsage}
                        onChange={handleChange}
                        placeholder="请输入预估月用量"
                      />
                    </div>
                  </div>
                </div>

                <div className="mb-6">
                  <Label htmlFor="description">描述</Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="请描述您的具体需求..."
                    rows={4}
                  />
                </div>

                <div className="flex justify-end">
                  <Button
                    type="submit"
                    size="lg"
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Send className="mr-2 h-5 w-5" />
                    提交申请
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
